import React, { useEffect, useMemo, useState } from "react";
import { RefreshCw, Video } from "lucide-react";

import { apiPatch } from "../../Api/Client.js";
import { formatDateTime } from "../../Shared/ScreenUtils.jsx";
import { parseChecklist, serializeChecklist } from "./lmsLeadForm.js";
import { buildCallContext, playbookHref } from "./leadCallContextModel.js";

export { actionItemText, buildCallContext, playbookHref, recordingDate, recordingSummary } from "./leadCallContextModel.js";

function compactList(values = []) {
  return values
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(", ");
}

function OnDemandPlayer({ url, title }) {
  const [open, setOpen] = useState(false);
  if (!url) return null;
  return (
    <div className="Lms-CallContext-Play">
      <button
        type="button"
        className="Soft-Button Small"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        {open ? "Hide player" : "Play"}
      </button>
      <a className="Soft-Button Small" href={url} target="_blank" rel="noreferrer">Open</a>
      {open ? (
        <iframe
          className="Lms-CallContext-Embed"
          src={url}
          // The recorder's own player needs its scripts; nothing else (no forms, pop-ups or top navigation).
          sandbox="allow-scripts allow-same-origin allow-presentation"
          title={title || "Recording"}
          loading="lazy"
          // No allow="autoplay" — player mounts only after Play, and the
          // recorder watch page itself does not autoplay.
          referrerPolicy="no-referrer-when-downgrade"
        />
      ) : null}
    </div>
  );
}

function syncStatusOf(recording) {
  return recording.ai_sync_status
    || (/fail|error/i.test(recording.ai_status || "") ? "failed"
      : /ready|complete|passed/i.test(recording.ai_status || "") ? "ready"
        : "processing");
}

/**
 * Lead-detail Call context: ordered recordings + notes, open commitments,
 * 1–3 next steps, on-demand playback. Uses only LMS data already on the page.
 */
export function LeadCallContextPanel({
  lead,
  onFlash,
  events = [],
  recordings = [],
  notes = [],
  eventsLoading = false,
  recordingsLoading = false,
  onReload,
}) {
  const [checklistValue, setChecklistValue] = useState(lead?.actionItem || lead?.action_item || "");
  const [checklistBusy, setChecklistBusy] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    setChecklistValue(lead?.actionItem || lead?.action_item || "");
    setExpandedId(null);
  }, [lead?.id, lead?.actionItem, lead?.action_item]);

  const context = useMemo(
    () => buildCallContext({ recordings, notes, checklistValue }),
    [recordings, notes, checklistValue],
  );

  const toggleCommitment = async (label) => {
    const items = parseChecklist(checklistValue);
    const normalized = String(label || "").trim().toLowerCase();
    let next;
    const index = items.findIndex((item) => item.label.trim().toLowerCase() === normalized);
    if (index >= 0) {
      next = items.map((item, itemIndex) => (itemIndex === index ? { ...item, done: !item.done } : item));
    } else {
      next = [...items, { id: `task-new-${normalized}`, done: false, label: String(label || "").trim() }];
    }
    const serialized = serializeChecklist(next);
    setChecklistBusy(normalized);
    try {
      await apiPatch(`/Banao/LeadAccounts/${lead.id}/`, { action_item: serialized });
      setChecklistValue(serialized);
    } catch (error) {
      onFlash?.(error?.payload?.message || error?.message || "Unable to update action item.");
    } finally {
      setChecklistBusy("");
    }
  };

  const upcoming = (events || []).filter((event) => event.starts_at && new Date(event.starts_at).getTime() >= Date.now());
  const loading = eventsLoading || recordingsLoading;

  return (
    <section className="Lms-Detail-Card Lms-CallContext" style={{ marginBottom: 14 }}>
      <header className="Lms-Calendar-Header">
        <div>
          <h3><Video size={17} /> Call context</h3>
          <p>Progression across attached recordings and lead notes — what moved, who appeared, what is still open.</p>
        </div>
        <button className="Soft-Button Small" disabled={loading} onClick={onReload}>
          <RefreshCw size={13} /> Refresh
        </button>
      </header>

      {(context.nextSteps.length > 0 || context.openCommitments.length > 0 || context.whoAppeared.length > 0) && (
        <div className="Lms-CallContext-Brief">
          {context.nextSteps.length > 0 && (
            <div className="Lms-CallContext-BriefBlock">
              <h4>Next steps</h4>
              <ol>
                {context.nextSteps.map((step) => (
                  <li key={`next-${step}`}>{step}</li>
                ))}
              </ol>
            </div>
          )}
          {context.openCommitments.length > 0 && (
            <div className="Lms-CallContext-BriefBlock">
              <h4>Open commitments</h4>
              <div className="Lms-Intelligence-Actions">
                {context.openCommitments.slice(0, 8).map((item) => {
                  const task = context.checklist.find((entry) => entry.label.trim().toLowerCase() === item.label.trim().toLowerCase());
                  return (
                    <label key={`open-${item.label}`}>
                      <input
                        type="checkbox"
                        checked={Boolean(task?.done)}
                        disabled={Boolean(checklistBusy)}
                        onChange={() => toggleCommitment(item.label)}
                      />
                      <span>{item.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          {context.whoAppeared.length > 0 && (
            <div className="Lms-CallContext-BriefBlock">
              <h4>Who from their side</h4>
              <p className="Lms-CallContext-People">
                {context.whoAppeared.map((person) => person.name).join(" · ")}
              </p>
            </div>
          )}
        </div>
      )}

      {(context.room?.reads?.length > 0 || context.room?.gaps?.length > 0) && (
        <div className="Lms-CallContext-Room">
          <h4>If you were not in the room</h4>
          {context.room.gaps.map((gap) => (
            <p key={gap.text} className="Lms-CallContext-Moved">
              {gap.text}{" "}
              <a href={playbookHref(gap.playbook.slug)}>Playbook: {gap.playbook.label}</a>
              <span className="Lms-CallContext-Medd"> MEDDPICC: {gap.playbook.meddpicc}</span>
            </p>
          ))}
          {context.room.reads.map((read) => (
            <p key={`read-${read.id}`} className="Lms-CallContext-Moved">
              {read.text}{" "}
              <a href={playbookHref(read.playbook.slug)}>Playbook: {read.playbook.label}</a>
              <span className="Lms-CallContext-Medd"> MEDDPICC: {read.playbook.meddpicc}</span>
            </p>
          ))}
        </div>
      )}

      {upcoming.length > 0 && (
        <div className="Lms-CallContext-Upcoming">
          <h4>Upcoming</h4>
          {upcoming.map((event) => (
            <article key={event.id} className="Lms-CallContext-UpcomingRow">
              <strong>{event.title || `Meeting ${event.id}`}</strong>
              <span>{formatDateTime(event.starts_at)}</span>
              {event.meeting_url ? <a href={event.meeting_url} target="_blank" rel="noreferrer">Join</a> : null}
            </article>
          ))}
        </div>
      )}

      <div className="Lms-CallContext-Timeline">
        <h4>Progression</h4>
        {context.timeline.map((entry, index) => {
          if (entry.kind === "note") {
            return (
              <article key={entry.id} className="Lms-CallContext-Row note">
                <div className="Lms-CallContext-RowTop">
                  <span className="Lms-CallContext-Kind">Note</span>
                  <span className="Lms-CallContext-When">{formatDateTime(entry.at)}</span>
                </div>
                <p className="Lms-CallContext-Moved">{entry.snippet || "Lead note"}</p>
              </article>
            );
          }

          const recording = entry.recording;
          const syncStatus = syncStatusOf(recording);
          const isOpen = expandedId === entry.id;
          const prior = index > 0 ? context.timeline[index - 1] : null;
          const movedLabel = prior
            ? (entry.newcomers?.length
              ? `New faces: ${entry.newcomers.join(", ")}`
              : entry.whatMoved || "Follow-up touch")
            : (entry.whatMoved || "First attached recording");

          return (
            <article key={entry.id} className={`Lms-CallContext-Row recording ${isOpen ? "open" : ""}`}>
              <div className="Lms-CallContext-RowTop">
                <div>
                  <strong>{recording.title || `Recording ${recording.external_recording_id}`}</strong>
                  <span className="Lms-CallContext-When">
                    {formatDateTime(entry.at)}
                    {recording.duration_seconds ? ` · ${Math.round(recording.duration_seconds / 60)} min` : ""}
                  </span>
                  <span className={`Lms-AI-Status ${syncStatus}`}>{syncStatus}</span>
                </div>
                <div className="Lms-Recorder-Actions">
                  <button
                    type="button"
                    className="Soft-Button Small"
                    onClick={() => setExpandedId(isOpen ? null : entry.id)}
                  >
                    {isOpen ? "Less" : "Details"}
                  </button>
                </div>
              </div>
              <p className="Lms-CallContext-Moved">{movedLabel}</p>
              {entry.people?.length > 0 && (
                <small className="Lms-CallContext-PeopleLine">On call: {compactList(entry.people)}</small>
              )}
              {isOpen && (
                <div className="Lms-CallContext-Detail">
                  {recording.rep_note ? <p className="Lms-Intelligence-RepNote">{recording.rep_note}</p> : null}
                  {entry.summary
                    ? <p>{entry.summary}</p>
                    : (
                      <p className={`Lms-Intelligence-State ${syncStatus === "failed" ? "failed" : ""}`}>
                        {syncStatus === "failed"
                          ? (recording.sync_error || "AI processing failed. Retry from the attachment tools.")
                          : syncStatus === "processing" || syncStatus === "pending"
                            ? "Summary processing — this row updates when the recorder finishes."
                            : "No summary yet."}
                      </p>
                    )}
                  {entry.actions.length > 0 && (
                    <div className="Lms-Intelligence-Actions">
                      {entry.actions.map((label) => {
                        const task = context.checklist.find((entryItem) => entryItem.label.trim().toLowerCase() === label.trim().toLowerCase());
                        return (
                          <label key={`${entry.id}-${label}`}>
                            <input
                              type="checkbox"
                              checked={Boolean(task?.done)}
                              disabled={!task || Boolean(checklistBusy)}
                              onChange={() => toggleCommitment(label)}
                            />
                            <span>{label}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  <OnDemandPlayer
                    url={recording.recording_url}
                    title={recording.title || "Recording"}
                  />
                </div>
              )}
            </article>
          );
        })}
        {!context.timeline.length && (
          <div className="Lms-Calendar-Empty">
            {recordingsLoading
              ? "Loading recordings…"
              : "No recordings or notes on this lead yet. Attach a recording under Calendar & Meeting Recording, or add a note below."}
          </div>
        )}
      </div>
    </section>
  );
}
